import SectionContentWrapper from "@/components/Shared/SectionContentWrapper";
import styled, { css } from "styled-components";
import SimpleMarquee, { SimpleMarqueeProps } from "@/components/Shared/AnimatedMarquee";
import mq from "@/utils/mq";
import NextLazyImage from "@/components/Core/NextLazyImage";
import { useEffect, useState } from "react";

const MarqueeContainer = styled.div`
  margin-top: 86px;
  position: relative;
  ${mq.mobile(css`
    margin-top: 42px;
  `)};
`;

const FadeOverlay = styled.div<{ $side: "left" | "right" }>`
  position: absolute;
  top: 0;
  ${(props) => props.$side}: 0;
  width: 400px;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  background: ${(props) =>
    props.$side === "left"
      ? "linear-gradient(90deg, #08090A 1.5%, rgba(8, 9, 10, 0.00) 100%)"
      : "linear-gradient(270deg, #08090A 1.5%, rgba(8, 9, 10, 0.00) 100%)"};
  ${mq.mobile(css`
    width: 15vw;
  `)};
`;

interface MarqueeImageObjectProps {
  $height: number;
}

const MarqueeImageObject = styled.div<MarqueeImageObjectProps>`
  height: ${(props) => props.$height}px;
  position: relative;
  width: fit-content;
  margin: 0 24px;
  user-select: none;
  pointer-events: none;
  display: flex;
  align-items: center;
  img {
    filter: invert(1) opacity(0.5);
  }
  ${mq.mobile(css`
    margin: 0 16px;
  `)};
`;

export default function LogoMarqueeWithTitle({ sectionTitle, logos }: { sectionTitle?: any; logos: any[] }) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const MOBILE_BREAKPOINT = 768;
  const DESKTOP_HEIGHT = 32;
  const MOBILE_HEIGHT = 24;

  useEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isMobile === null || newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };

    checkMobile();

    const resizeObserver = new ResizeObserver(() => {
      checkMobile();
    });

    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, [isMobile]);

  const logoHeight = isMobile ? MOBILE_HEIGHT : DESKTOP_HEIGHT;

  const marqueeProps: Partial<SimpleMarqueeProps> = {
    direction: "left",
    slowdownOnHover: false,
    grabCursor: false,
    baseVelocity: 0.5,
  };

  return (
    <SectionContentWrapper
      sectionTitle={sectionTitle?.[0] ? [{ ...sectionTitle[0], spacing: "expanded", noBottomPadding: true }] : []}
      theme={"dark"}
      noPaddingTop={true}
      noBottomPadding={true}
      darkMode>
      <MarqueeContainer>
        <FadeOverlay $side='left' />
        <FadeOverlay $side='right' />
        <SimpleMarquee {...marqueeProps}>
          {logos.map((logo) => (
            <MarqueeImageObject key={logo.id} $height={logoHeight}>
              <NextLazyImage
                src={logo.filename}
                alt={logo.alt}
                fitHeight={logoHeight}
                style={{ objectFit: "contain" }}
              />
            </MarqueeImageObject>
          ))}
        </SimpleMarquee>
      </MarqueeContainer>
    </SectionContentWrapper>
  );
}
